import { FC, useCallback, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LS from 'utils/bom-dom-manipulation/local-storage';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import { SEPARATED_WIDGETS } from 'utils/constants/sportsbook/generic';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import {
  getMountPath,
  mountPoint,
  sbPathFromObj
} from 'utils/generic/route-manipulation';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { handleRedirectToSportsBook } from 'utils/sportsbook/handleRedirectToSportsBook';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { GlobalIcon } from 'components/GlobalIcon';
import { CompetitionNameWithTooltip } from 'components/Shared/CompetitionNameWithTooltip';
import { NoItem } from 'components/Shared/NoItem';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  batchActions,
  setTimeFilterData,
  setTournamentSportIds
} from 'store/actions';
import {
  setGamesByCompetitions,
  setSelectedCompetitions,
  setSportRegionCompData,
  setSportsbookSelectedSport
} from 'store/actions/new-sportsbook';
import {
  getSelectedCompetitions,
  getSportsbookSelectedSport
} from 'store/selectors/new-sportsbook';
import {
  getTimeFiltersData,
  getTournamentSportIds
} from 'store/selectors/sport-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TProps = {
  sportData: TSportDataWithCompetitions;
  fromSportsList?: boolean;
  openFromSideBarPopup?: boolean;
  sportIconSize?: number;
  regionIconSize?: number;
};

type TCompetitionInfo = {
  favorite_order: number;
  id: number;
  name: string;
  order: number;
  region: string;
  regionId: number;
};

export type TSportDataWithCompetitions = {
  [k: string]: {
    id: number;
    type: number;
    name: string;
    alias: string;
    order: number;
    competition: {
      [k: string]: TCompetitionInfo;
    };
  };
};

export const TopLeagueCompactLayout: FC<TProps> = ({
  sportData,
  fromSportsList = false,
  openFromSideBarPopup,
  sportIconSize,
  regionIconSize
}) => {
  const selectedSport = useSelector(getSportsbookSelectedSport);
  const configs = useSportsbookConfigs();
  const tournamentSportIds = useSelector(getTournamentSportIds);
  const timeStamp = useSelector(getTimeFiltersData);

  const { t } = useTranslation();
  const { history, push } = useCustomHistory();
  const dispatch = useDispatch();
  const { path, params } = useSbRouteMatch();
  const currentSportAlias = selectedSport?.alias || params?.sport;
  const routeParams = useSbRouteMatch(
    `${mountPoint(window.getPathname())}/:sport?/:region?/:competition?/:game?`
  );

  const selectedCompetitions = useSelector(getSelectedCompetitions)?.[
    currentSportAlias
  ];

  useLayoutEffect(() => {
    if (configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED) {
      return;
    }

    const selectedSportToUpdate = Object.values(sportData).find(
      sport => sport.alias === currentSportAlias
    );

    if (
      selectedSportToUpdate &&
      selectedSportToUpdate?.alias !== selectedSport?.alias
    ) {
      dispatch(setSportsbookSelectedSport(selectedSport));
    }
  }, [sportData, params.sport]);

  const handleClick = useCallback(
    (sportAlias: string, compId: number, region: string) => {
      if (timeStamp && Object.keys(timeStamp).length) {
        dispatch(
          setTimeFilterData({
            option: 'All',
            date: false,
            count: undefined,
            name: t('sportsbook.allEvents')
          })
        );
      }

      if (configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED) {
        const newPath = `${mountPoint(window.getPathname())}?compIds=${compId}`;
        push(newPath);

        return;
      }

      const currentSportId = Object.values(sportData).find(
        sport => sport.alias === sportAlias
      )?.id;

      const isSameCompetitionForEU =
        selectedCompetitions?.length === 1 &&
        selectedCompetitions[0] === compId;

      const isSameRouteForClassicAndUSA =
        !selectedCompetitions && +params?.competition === +compId;

      if (isSameCompetitionForEU || isSameRouteForClassicAndUSA) {
        return;
      }

      if (!tournamentSportIds?.includes(currentSportId || null)) {
        dispatch(setTournamentSportIds(null));
      }

      dispatch(
        batchActions(
          setSportRegionCompData(null, true),
          setGamesByCompetitions(null),
          setSelectedCompetitions({
            data: null,
            key: currentSportAlias
          })
        )
      );
      LS.setItem(storageKeyName('sportsbook', 'EU_SPORT_ALIAS'), sportAlias);

      if (openFromSideBarPopup) {
        if (!document.querySelector('.prematch__type-page')) {
          handleRedirectToSportsBook(
            'prematch',
            region,
            compId,
            undefined,
            sportAlias
          );
        } else {
          const pathname = getMountPath(
            routeParams?.path?.replace(/:sport\?.*$/, sportAlias) || ''
          );

          history.push(`${pathname}/${region}/${compId}`);
        }
      } else {
        if (!fromSportsList) {
          const pathToRedirect = SEPARATED_WIDGETS.IS_SEPARATED_WIDGETS
            ? routeParams.path
            : path;

          let pathname = `${mountPoint(
            pathToRedirect
          )}/${sportAlias}/${region}/${compId}`;

          if (isMobile()) {
            pathname = `${mountPoint(path)}/${
              ENotRealSports.topLeague
            }/${sportAlias}/${region}/${compId}`;
          }

          history.push({ pathname });
        } else {
          handleRedirectToSportsBook(
            'prematch',
            region,
            compId,
            undefined,
            sportAlias
          );
        }
      }
    },
    [
      path,
      history,
      params?.sport,
      params?.competition,
      selectedSport?.alias,
      selectedCompetitions,
      tournamentSportIds,
      timeStamp
    ]
  );

  const data = useMemo(() => {
    return Object.values(sportData || {})
      .map(sport =>
        Object.values(sport?.competition || {}).map(competition => {
          return {
            ...competition,
            favorite_order: competition.favorite_order ?? Infinity,
            sportAlias: sport.alias
          };
        })
      )
      .flat();
  }, [sportData]);

  useLayoutEffect(() => {
    const shouldSelectFirstCompetition =
      !fromSportsList &&
      !isMobile() &&
      !routeParams.params.sport &&
      SpringConfigs.SPORT_MENU_DEFAULT_SELECTED === 'topLeagues' &&
      configs.spbLayoutType !== ESportsbookLayoutTypes.ADVANCED;

    const hasTopLeagueData = !!data.length;

    if (shouldSelectFirstCompetition) {
      if (hasTopLeagueData) {
        const { sportAlias, region, id } = data[0];
        handleClick(sportAlias, id, region);
      } else {
        history.push(`${mountPoint(path)}/${ENotRealSports.topLeague}`);
      }
    }
  }, [data, routeParams.params.sport]);

  if (!Object.keys(sportData).length) {
    return (
      <NoItem
        title={t('sportsbook.noGame')}
        text={t('sportsbook.noGameText')}
        size="medium"
      />
    );
  }

  return (
    <div className="competitions-by-lists">
      <div className="competitions-by-lists__content">
        {sortByOrder(data, 'favorite_order').map(competition => (
          <div key={competition.id} className="competition-item-wrapper">
            <CompetitionNameWithTooltip
              clickHandler={() => {
                handleClick(
                  competition.sportAlias,
                  competition.id,
                  competition.region
                );
              }}
              competitionUrl={`${SpringConfigs.PAGE_URLS.prematch}/${
                SpringConfigs.SPORTSBOOK_MOUNT_PATH
              }${sbPathFromObj({
                sport: competition.sportAlias,
                competition: `${competition.id}`,
                region: competition.region
              })}`}
              competitionItem={competition}
              active={
                competition.id === +params.competition ||
                selectedCompetitions?.includes(competition.id)
              }
              IconBeforeText={
                <>
                  <CompetitionsFavorite
                    iconSize={EIconSize._16}
                    competitionInfo={{
                      id: competition.id,
                      name: competition.name,
                      regionAlias: competition.region,
                      sportAlias: competition.sportAlias
                    }}
                  />
                  <GlobalIcon
                    name={competition.sportAlias}
                    lib="sports"
                    size={sportIconSize || EIconSize._16}
                  />
                </>
              }
              IconAfterText={
                <GlobalIcon
                  lib="flags"
                  name={competition.region}
                  size={regionIconSize || EIconSize._16}
                />
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};
